import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search } from "lucide-react";
import { useAppSelector } from "@/store/hooks";
import type { WalletAsset } from "@/interfaces/asset";
import { formatCurrency, formatEvmHumanBalance } from "@/utils";
import { removeTrailingToken } from "@/utils";
import { getAvailableBalance, searchAssets } from "./helpers";

interface Props {
  assets: WalletAsset[];
  value?: WalletAsset;
  onChange: (asset: WalletAsset) => void;
  label?: string;
  placeholder?: string;

  /**
   * Assets to exclude from the dropdown.
   * Useful for Swap "To Asset" so the source asset
   * cannot be selected again.
   */
  excludeIds?: string[];
}

const AssetSelector = ({
  assets,
  value,
  onChange,
  label = "Asset",
  placeholder = "Search asset...",
  excludeIds = [],
}: Props) => {
  const { theme } = useAppSelector((state) => state.ui);
  const [query, setQuery] = useState(value?.token?.symbol ?? "");
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setQuery(value?.token?.symbol ?? "");
  }, [value]);

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handler);

    return () => {
      document.removeEventListener("mousedown", handler);
    };
  }, []);

  const availableAssets = useMemo(() => {
    return assets.filter((asset) => !excludeIds.includes(asset.id));
  }, [assets, excludeIds]);

  const filteredAssets = useMemo(() => {
    return searchAssets(availableAssets, query);
  }, [availableAssets, query]);

  return (
    <div ref={wrapperRef} className="relative">
      <label
        className={`block text-sm mb-2 ${
          theme === "dark" ? "text-muted-foreground" : "text-gray-500"
        }`}
      >
        {label}
      </label>
      <div className="relative">
        <Search
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
        />

        <input
          value={query}
          placeholder={placeholder}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          className={`w-full rounded-lg border py-3 pl-10 pr-28 text-sm focus:outline-none focus:border-primary h-14 ${
            theme === "dark"
              ? "bg-muted border-border text-foreground placeholder:text-muted-foreground"
              : "bg-white border-gray-200 text-gray-900 placeholder-gray-400"
          }`}
        />

        {/* Dropdown trigger */}
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          className="absolute right-0 top-0 h-full flex items-center cursor-pointer"
          aria-label={
            open ? "Close selection dropdown" : "Open selection dropdown"
          }
        >
          <div
            className={`h-6 w-px ${
              theme === "dark" ? "bg-border" : "bg-gray-200"
            }`}
          />

          <div className="flex items-center gap-1.5 px-3 text-sm font-semibold text-primary">
            <span>Select</span>

            <ChevronDown
              size={18}
              strokeWidth={2.5}
              className={`transition-transform duration-200 ${
                open ? "rotate-180" : "rotate-0"
              }`}
            />
          </div>
        </button>
      </div>

      {open && (
        <div
          data-lenis-prevent
          className={`absolute z-30 mt-2 w-full overflow-y-auto rounded-lg border max-h-80 shadow-lg ${
            theme === "dark"
              ? "border-border bg-card"
              : "border-gray-200 bg-white"
          }`}
        >
          {filteredAssets.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <p className="text-sm font-medium text-muted-foreground">
                No matching assets
              </p>

              <p className="text-xs text-muted-foreground mt-1">
                Try another search.
              </p>
            </div>
          ) : (
            filteredAssets.map((asset) => {
              const balance = getAvailableBalance(asset);

              const icon = asset.iso2
                ? `https://flagcdn.com/w80/${asset.iso2.toLowerCase()}.png`
                : (asset.token.icon ?? undefined);

              const networkIcon =
                asset.token.blockchainNetwork?.icon ?? undefined;

              return (
                <button
                  key={asset.id}
                  type="button"
                  onClick={() => {
                    onChange(asset);
                    setQuery(asset.token.symbol);
                    setOpen(false);
                  }}
                  className={`flex w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-muted justify-between ${
                    value?.id === asset.id ? "bg-bg-muted" : "hover:bg-bg-muted"
                  }`}
                >
                  <div className="flex gap-3">
                    <div className="relative">
                      <img
                        src={icon}
                        alt={asset.token.name}
                        className="h-12 w-12 rounded-full object-cover"
                      />

                      {!asset.iso2 && networkIcon && (
                        <img
                          src={networkIcon}
                          alt={asset.token.blockchainNetwork?.name}
                          className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full border-2 border-background bg-background"
                        />
                      )}
                    </div>

                    <div>
                      <div className="font-medium">
                        {removeTrailingToken(asset.token.name)}
                      </div>

                      <div className="text-xs text-muted-foreground">
                        {asset.token.symbol} •{" "}
                        {asset.token.blockchainNetwork?.name}
                      </div>
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="text-xs text-muted-foreground">
                      Available
                    </div>

                    <div className="font-semibold text-emerald-500">
                      {formatEvmHumanBalance(balance, asset?.token?.isNative)}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default AssetSelector;
